/**
 * Service centralisé pour la gestion des tenants (organisations)
 * Ce service facilite la création, mise à jour et gestion des tenants
 * tout en assurant la cohérence entre les différentes méthodes d'accès.
 */

import { Tenant, TenantPlan, TenantStatus } from './types';
import tenantAPI from './tenant-api';
import tenantManager from './tenant-manager';
import { TokenManager } from '../auth/token-manager';
import UserDataManager from '../auth/user-data-manager';
import { toast } from 'react-hot-toast';

export class TenantService {
  private static instance: TenantService;
  private tenants: Tenant[] = [];
  private tenantAPI = tenantAPI;

  private constructor() {}

  /**
   * Retourne l'instance unique du service (Singleton)
   */
  public static getInstance(): TenantService {
    if (!TenantService.instance) {
      TenantService.instance = new TenantService();
    }
    return TenantService.instance;
  }
  
  /**
   * Génère un slug à partir d'un nom
   * @param name Le nom à partir duquel générer le slug
   * @returns Le slug généré
   */
  private generateSlug(name: string): string {
    return (
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '') +
      '-' +
      Math.random().toString(36).substring(2, 10)
    );
  }

  /**
   * Vérifie si un tenant est un tenant de test
   * @param tenant Le tenant à vérifier
   * @returns true si c'est un tenant de test
   */
  private isTestTenant(tenant: Tenant): boolean {
    return (
      tenant.id.includes('test') ||
      ((tenant.name?.toLowerCase().includes('test') ?? false) || false) ||
      /(001|002|003)$/.test(tenant.id)
    );
  }
  
  /**
   * Vérifie si l'application est actuellement limitée par le rate limiter
   * @returns true si l'application est actuellement limitée
   */
  private checkRateLimit(): boolean {
    try {
      const rateLimitData = localStorage.getItem('sentinel_rate_limit_data');
      
      if (rateLimitData) {
        const { timestamp, retryAfter } = JSON.parse(rateLimitData);
        const now = Date.now();
        
        if (now < timestamp + retryAfter) {
          // Toujours en période de limitation
          const remainingSeconds = Math.ceil((timestamp + retryAfter - now) / 1000);
          console.log(`⚠️ TenantService - Rate limiting actif pour encore ${remainingSeconds} secondes`);
          throw new Error(`Trop de requêtes. Veuillez réessayer dans ${remainingSeconds} secondes.`);
        } else {
          // La période de limitation est terminée
          localStorage.removeItem('sentinel_rate_limit_data');
        }
      }
      
      return false;
    } catch (e: unknown) {
      if (e instanceof Error && e.message.includes('Trop de requêtes')) {
        throw e; // Propager l'erreur de limitation
      }
      
      console.warn('Erreur lors de la vérification du rate limiting:', e);
      return false;
    }
  }
  
  /**
   * Marque l'application comme étant limitée par le rate limiter
   * @param retryAfter Délai en ms avant de pouvoir réessayer
   */
  private markRateLimited(retryAfter: number): void {
    try {
      localStorage.setItem('sentinel_rate_limit_data', JSON.stringify({
        timestamp: Date.now(),
        retryAfter: retryAfter
      }));
    } catch (e: unknown) {
      console.error('Erreur lors du marquage de limitation de taux:', e);
    }
  }
  
  /**
   * Réinitialise les compteurs de tentatives
   */
  private resetRateLimitCounters(): void {
    try {
      localStorage.removeItem('sentinel_tenant_creation_attempts');
    } catch (e: unknown) {
      // Ignorer les erreurs de localStorage
    }
  }

  /**
   * Obtient la liste des plans disponibles côté backend
   * @returns Liste des plans disponibles
   */
  public async getAvailablePlans(): Promise<TenantPlan[]> {
    // Par défaut, nous proposons ces plans, mais le backend peut avoir une liste différente
    const defaultPlans: TenantPlan[] = ['free', 'standard', 'professional', 'enterprise'];
    
    try {
      // On pourrait implémenter ici un appel au backend pour récupérer la liste des plans disponibles
      // Pour l'instant, on utilise les plans par défaut définis dans le frontend
      return defaultPlans;
    } catch (error) {
      console.warn('🏢 TenantService: Impossible de récupérer les plans disponibles:', error);
      return defaultPlans;
    }
  }

  /**
   * Suggère un plan alternatif quand le plan demandé n'est pas disponible
   * @param currentPlan Le plan demandé initialement
   * @returns Un plan alternatif ou null si aucun n'est disponible
   */
  public async suggestAlternativePlan(currentPlan: TenantPlan): Promise<TenantPlan | null> {
    // Ordre de priorité des plans (du moins cher au plus cher)
    const planOrder: TenantPlan[] = ['free', 'standard', 'professional', 'enterprise'];
    
    try {
      // Récupérer tous les tenants existants de l'utilisateur
      const existingTenants = await this.getAllTenants({ forceRefresh: false });
      
      // Compter combien de tenants de chaque plan l'utilisateur possède déjà
      const planCounts: Record<TenantPlan, number> = {
        free: 0,
        standard: 0,
        professional: 0,
        enterprise: 0
      };
      
      existingTenants.forEach(tenant => {
        if (tenant.plan && planCounts[tenant.plan] !== undefined) {
          planCounts[tenant.plan]++;
        }
      });
      
      // Trouver l'index du plan actuel dans l'ordre de priorité
      const currentIndex = planOrder.indexOf(currentPlan);
      if (currentIndex === -1) return null;
      
      // Chercher le prochain plan disponible dans l'ordre de priorité
      for (let i = currentIndex + 1; i < planOrder.length; i++) {
        const nextPlan = planOrder[i];
        // Vérifier si l'utilisateur peut créer un tenant avec ce plan
        const canCreate = await this.canCreateTenantWithPlan(nextPlan);
        if (canCreate) {          
          return nextPlan;
        }
      }
      
      return null; // Aucun plan alternatif disponible
    } catch (error) {
      console.error('🏢 TenantService: Erreur lors de la suggestion de plan alternatif:', error);
      return null;
    }
  }

  /**
   * Crée un nouveau tenant (organisation) et synchronise les contextes
   * @param tenantData Les données du tenant à créer
   * @param tryAlternativePlans Si true, essaie automatiquement des plans alternatifs en cas d'échec
   * @returns Le tenant créé
   */
  public async createTenant(tenantData: Partial<Tenant>, tryAlternativePlans: boolean = false): Promise<Tenant | null> {
    try {
      console.log('🏢 TenantService: Création d\'un nouveau tenant');

      // 1. Vérifier l'authentification avec gestion améliorée post-activation
      if (!TokenManager.isAuthenticated()) {
        console.warn('⚠️ TokenManager indique que l\'utilisateur n\'est pas authentifié');
        
        // Vérifier si nous sommes dans un contexte post-activation ou post-onboarding
        let isPostActivation = false;
        let isPostOnboarding = false;
        
        try {
          isPostActivation = localStorage.getItem('sentinel_post_activation') === 'true' || 
                            localStorage.getItem('sentinel_post_activation') === 'verified';
          isPostOnboarding = localStorage.getItem('sentinel_post_onboarding') === 'true';
        } catch (e) {
          console.warn('⚠️ Erreur lors de la vérification des marqueurs localStorage:', e);
        }
        
        // Si nous sommes dans un contexte post-activation ou post-onboarding, tenter de récupérer les tokens
        if (isPostActivation || isPostOnboarding) {
          console.log('🔍 Contexte post-activation/onboarding détecté, tentative de récupération des tokens');
          
          try {
            const accessToken = localStorage.getItem('sentinel_auth_token');
            const refreshToken = localStorage.getItem('sentinel_refresh_token');
            
            if (accessToken && refreshToken) {
              console.log('🔑 Tokens trouvés dans localStorage, restauration dans TokenManager');
              TokenManager.setTokens({
                access_token: accessToken,
                refresh_token: refreshToken
              });
              TokenManager.synchronizeTokens();
              
              // Vérifier si la restauration a fonctionné
              if (TokenManager.isAuthenticated()) {
                console.log('✅ Authentification rétablie avec succès');
              } else {
                console.error('❌ Échec de rétablissement de l\'authentification malgré la présence de tokens');
                throw new Error('Authentification échouée malgré la présence de tokens. Veuillez vous reconnecter.');
              }
            } else {
              console.error('❌ Aucun token trouvé dans localStorage');
              throw new Error('Tokens d\'authentification non trouvés. Veuillez vous reconnecter.');
            }
          } catch (authError) {
            console.error('❌ Erreur lors de la tentative de restauration de l\'authentification:', authError);
            throw new Error('Problème d\'authentification. Veuillez vous reconnecter.');
          }
        } else {
          throw new Error('Utilisateur non authentifié');
        }
      }

      // 2. Vérifier si l'utilisateur est autorisé à créer un tenant avec ce plan
      if (tenantData.plan) {
        const canCreate = await this.canCreateTenantWithPlan(tenantData.plan);
        if (!canCreate) {
          // Si tryAlternativePlans est activé, tenter de trouver un plan alternatif
          if (tryAlternativePlans) {
            const alternativePlan = await this.suggestAlternativePlan(tenantData.plan);
            if (alternativePlan) {
              console.log(`🏢 TenantService: Plan ${tenantData.plan} non disponible, tentative avec plan ${alternativePlan}`);
              tenantData.plan = alternativePlan;
            } else {
              // Aucun plan alternatif disponible, on affiche un message d'erreur
              if (tenantData.plan === 'free') {
                throw new Error('Vous avez déjà créé une organisation gratuite. Veuillez choisir un plan payant.');
              } else {
                throw new Error(`Limite de création atteinte pour le plan ${tenantData.plan}`);
              }
            }
          } else {
            // Message d'erreur spécifique pour le plan gratuit
            if (tenantData.plan === 'free') {
              throw new Error('Vous avez déjà créé une organisation gratuite. Veuillez choisir un plan payant.');
            } else {
              throw new Error(`Limite de création atteinte pour le plan ${tenantData.plan}`);
            }
          }
        }
      }

      // 3. Récupérer l'utilisateur actuel
      const userProfile = TokenManager.getUserProfile();
      if (!userProfile?.id) {
        throw new Error('Données utilisateur manquantes ou incomplètes');
      }
      const userId = String(userProfile.id);

      // 4. Générer un slug s'il n'en a pas déjà un
      if (!tenantData.slug && tenantData.name) {
        tenantData.slug = this.generateSlug(tenantData.name);
      }

      // 5. Définir les timestamps
      const now = new Date().toISOString();
      tenantData.createdAt ??= now;
      tenantData.updatedAt ??= now;

      // 6. Statut par défaut
      tenantData.status ??= 'active' as TenantStatus;

      // 7. Préparer les données finales pour l'API
      const finalTenantData: any = {
        ...tenantData,
      };

      // Ajouter le propriétaire si non défini
      if (!finalTenantData.owner) {
        finalTenantData.owner = userId;
      }

      // 8. Vérifier le rate limit
      this.checkRateLimit();

      // 9. Appel API pour créer le tenant
      console.log('🏢 TenantService: Appel à l\'API de création avec userId:', userId);
      const newTenant = await tenantAPI.createTenant(finalTenantData, userId);

      // 10. Force la récupération complète des tenants pour s'assurer que le nouveau tenant est visible
      try {
        await this.forceFullTenantRefresh(true, newTenant.id);
        console.log('✅ TenantService: Rafraîchissement complet des tenants après création réussi');
        
        // 10b. Définir automatiquement le nouveau tenant comme tenant par défaut
        try {
          console.log('🏢 TenantService: Définition du nouveau tenant comme tenant par défaut');
          await this.setDefaultTenant(newTenant.id);
          
          // Stocker l'ID du tenant par défaut dans localStorage pour persistance
          localStorage.setItem('sentinel_default_tenant_id', newTenant.id);
          localStorage.setItem('currentTenantId', newTenant.id);
          console.log(`✅ TenantService: Tenant par défaut défini avec succès: ${newTenant.name} (${newTenant.id})`);
        } catch (defaultError) {
          console.error('❌ TenantService: Erreur lors de la définition du tenant par défaut:', defaultError);
          // Continuer malgré l'erreur, car le tenant a été créé avec succès
        }
      } catch (refreshError) {
        // En cas d'échec du rafraîchissement complet, on utilise la méthode standard
        console.warn('⚠️ TenantService: Échec du rafraîchissement complet, utilisation de la synchronisation standard');
        await this.synchronizeTenants({ force: true });
        
        // Essayer de définir le tenant par défaut même après une synchronisation standard
        try {
          console.log('🏢 TenantService: Tentative de définition du tenant par défaut après synchronisation standard');
          await this.setDefaultTenant(newTenant.id);
          localStorage.setItem('sentinel_default_tenant_id', newTenant.id);
          localStorage.setItem('currentTenantId', newTenant.id);
        } catch (defaultError) {
          console.error('❌ TenantService: Erreur lors de la définition du tenant par défaut après synchronisation:', defaultError);
        }
      }

      // 11. Réinitialiser les compteurs de rate limit
      this.resetRateLimitCounters();

      toast.success('Organisation créée avec succès 🚀');
      return newTenant;
    } catch (error: unknown) {
      // Vérifier si c'est une erreur de rate limit
      if (error instanceof Error && error.message.includes('Trop de requêtes')) {
        const retryAfter = error.message.match(/dans (\d+) secondes/);
        if (retryAfter) {
          const seconds = parseInt(retryAfter[1], 10);
          this.markRateLimited(seconds * 1000);
          toast.error(`Trop de requêtes. Veuillez réessayer dans ${seconds} secondes.`);
        }
      }
      console.error('🏢 TenantService: Erreur lors de la création du tenant:', error);

      // Fonction helper pour détecter l'erreur de limite de tenants gratuits
      const isFreePlanLimitError = (err: any): boolean => {
        if (!err) return false;
        
        // Vérifier le cas où l'erreur est une chaîne de caractères
        if (typeof err === 'string') {
          return (
            err.includes('Vous avez déjà créé une organisation gratuite') ||
            err.includes('nombre maximum de tenants') ||
            err.includes('plan gratuit') ||
            err.includes('maximum number of tenants') ||
            err.includes('free plan limit')
          );
        }
        
        // Vérifier si c'est une Error
        if (err instanceof Error && err.message) {
          return isFreePlanLimitError(err.message);
        }
        
        // Vérifier si c'est un objet d'erreur API avec details
        if (typeof err === 'object') {
          // Vérifier si l'objet a une propriété 'details'
          if (err.details) {
            return isFreePlanLimitError(err.details);
          }
          
          // Vérifier si l'objet a une propriété 'plan'
          if (err.plan) {
            return isFreePlanLimitError(err.plan);
          }
          
          // Parcourir toutes les propriétés de l'erreur
          for (const key in err) {
            if (err[key] && typeof err[key] === 'object') {
              if (isFreePlanLimitError(err[key])) {
                return true;
              }
            } else if (err[key] && typeof err[key] === 'string') {
              if (isFreePlanLimitError(err[key])) {
                return true;
              }
            }
          }
        }
        
        return false;
      };
      
      // Erreur spécifique pour le plan invalide
      if (error instanceof Error && error.message.includes('"standard" is not a valid choice')) {
        console.log('🏢 TenantService: Plan standard invalide, tentative avec plan professional');
        
        // Remplacer standard par professional dans les données du tenant
        if (tenantData.plan && String(tenantData.plan) === 'standard') {
          tenantData.plan = 'professional' as TenantPlan;
          // Retenter la création avec le plan professional
          return this.createTenant(tenantData, false);
        }
      } else if (tryAlternativePlans) {
        // Extraire et analyser l'erreur pour détecter une limite de plan gratuit
        const errorObj = error as any;
        let isFreePlanLimit = false;
        
        // Vérifier l'erreur sous différents formats possibles
        if (errorObj && typeof errorObj === 'object') {
          // Inspecter l'objet d'erreur et ses propriétés
          console.log('🔍 TenantService - Détection détaillée de l\'erreur de plan gratuit:', JSON.stringify(errorObj));
          
          // Vérifier le format spécifique du backend Django
          if (errorObj.details && typeof errorObj.details === 'string') {
            console.log('- Détails de l\'erreur:', errorObj.details);
            
            // Détecter la structure spécifique du message d'erreur Python
            if (errorObj.details.includes("'plan'") && 
                errorObj.details.includes('ErrorDetail') && 
                errorObj.details.includes('Vous avez déjà créé une organisation gratuite')) {
              console.log('- Erreur de limite de plan gratuit détectée dans le format Django!');
              isFreePlanLimit = true;
            }
            // Détecter d'autres formes de messages
            else if (errorObj.details.includes('plan') && 
                     (errorObj.details.includes('gratuit') || 
                      errorObj.details.includes('maximum'))) {
              console.log('- Autre forme d\'erreur de plan détectée');
              isFreePlanLimit = true;
            }
          }
          
          // Vérifier directement dans le champ error
          if (!isFreePlanLimit && errorObj.error && typeof errorObj.error === 'string' && 
              errorObj.error.includes('organisation')) {
            console.log('- Message d\'erreur principal contient des indices:', errorObj.error);
            isFreePlanLimit = true;
          }
        }
        
        // Vérifier avec notre fonction générique
        if (!isFreePlanLimit) {
          isFreePlanLimit = isFreePlanLimitError(errorObj);
        }
        
        if (isFreePlanLimit) {
          // Si l'utilisateur a atteint la limite de tenants gratuits
          console.log('🏢 TenantService: Limite de plan gratuit atteinte, passage automatique au plan standard');
          tenantData.plan = 'standard' as TenantPlan;
          
          // Afficher un message pour informer l'utilisateur
          toast.success(
            'Limite de tenants gratuits atteinte. Nous passons automatiquement au plan standard pour vous permettre de créer cette organisation.',
            { duration: 7000 }
          );
          
          // Retenter la création avec le plan standard
          return this.createTenant(tenantData, false);
        }
      }

      // Gérer les erreurs de l'API qui pourraient contenir des informations structurées
      if (error instanceof Error && error.message) {
        toast.error(error.message);
      } else {
        toast.error('Erreur inconnue');
      }

      throw error;
    }
  }

  /**
   * Vérifie si l'utilisateur peut créer un tenant avec le plan spécifié
   * @param plan Le plan du tenant à créer
   * @returns true si l'utilisateur peut créer un tenant avec ce plan
   */
  public async canCreateTenantWithPlan(plan: TenantPlan): Promise<boolean> {
    try {
      // Vérifier l'authentification
      if (!TokenManager.isAuthenticated()) {
        return false;
      }

      // Récupérer tous les tenants existants de l'utilisateur
      const existingTenants = await this.getAllTenants({ forceRefresh: true });
      
      // Compter combien de tenants de chaque plan l'utilisateur possède déjà
      const planCounts: Record<TenantPlan, number> = {
        free: 0,
        standard: 0,
        professional: 0,
        enterprise: 0
      };
      
      existingTenants.forEach(tenant => {
        if (tenant.plan && planCounts[tenant.plan] !== undefined) {
          planCounts[tenant.plan]++;
        }
      });
      
      // Vérifier les limites selon le plan
      switch (plan) {
        case 'free':
          return planCounts.free === 0; // Un seul tenant gratuit autorisé
        case 'standard':
          return planCounts.standard < 2; // Max 2 tenants standard
        case 'professional':
          return planCounts.professional < 3; // Max 3 tenants professional
        case 'enterprise':
        default:
          return true; // Pas de limite sur enterprise (ou plan inconnu)
      }
    } catch (error: unknown) {
      console.error('🏢 TenantService: Erreur canCreateTenantWithPlan:', error);
      return false;
    }
  }

  /**
   * Récupère tous les tenants accessibles à l'utilisateur courant
   * @param options Options de récupération
   * @returns Liste des tenants
   */
  public async getAllTenants(options: { forceRefresh?: boolean, retryOnError?: boolean } = {}): Promise<Tenant[]> {
    const { forceRefresh = false, retryOnError = true } = options;
    
    // Si pas de rafraîchissement forcé et qu'on a des tenants en cache, les retourner
    if (!forceRefresh && this.tenants.length > 0) {
      return this.tenants;
    }
    
    try {
      console.log('🏢 TenantService: Récupération des tenants');
      
      // Vérifier la limite de taux avant d'effectuer une requête
      this.checkRateLimit();
      
      // Vérifier l'authentification
      const userProfile = TokenManager.getUserProfile();
      if (!userProfile?.id) {
        throw new Error('Données utilisateur manquantes ou incomplètes');
      }
      
      // Tenter de récupérer les tenants via l'API
      const tenants = await this.tenantAPI.getUserTenants();
      
      // Vérifier si la réponse est valide
      if (!Array.isArray(tenants)) {
        console.warn('🏢 TenantService: Format de réponse inattendu:', tenants);
        throw new Error('Format de réponse inattendu');
      }
      
      // Mettre en cache les tenants valides avec filtrage plus strict
      this.tenants = tenants.filter((t): t is Tenant => {
        // Vérifier que le tenant est un objet valide avec un ID
        if (!t || typeof t !== 'object' || !t.id) {
          console.warn('🏢 TenantService: Tenant invalide ignoré:', t);
          return false;
        }
        
        // Ignorer les tenants de test en production
        if (process.env.NODE_ENV === 'production') {
          const isTestTenant = (t.id.toString().toLowerCase().includes('test') || 
                             (t.name?.toLowerCase().includes('test') ?? false));
          if (isTestTenant) {
            console.log('🏢 TenantService: Tenant de test ignoré en production:', t.id);
            return false;
          }
        }
        
        return true;
      });
      
      // Sauvegarder dans le localStorage pour utilisation ultérieure
      try {
        localStorage.setItem('availableTenants', JSON.stringify(this.tenants));
        localStorage.setItem('lastTenantReloadTimestamp', Date.now().toString());
      } catch (storageError: unknown) {
        console.warn('🏢 TenantService: Erreur lors de la sauvegarde localStorage:', storageError);
      }
      
      // Mettre à jour le TenantManager avec les tenants récupérés
      this.updateTenantManager(this.tenants);
      
      // Remettre à zéro les compteurs de rate limit car la requête a réussi
      this.resetRateLimitCounters();
      
      // Filtrer les tenants de test si nécessaire (déjà fait dans le filtre mais par précaution)
      return this.filterTestTenants(this.tenants);
    } catch (error: unknown) {
      console.error('🏢 TenantService: Erreur lors de la récupération:', error);
      
      // En cas d'erreur, tenter d'utiliser les tenants en cache du localStorage
      if (retryOnError) {
        const cachedTenants = this.getTenantsFromLocalStorage();
        if (cachedTenants.length > 0) {
          console.log('🏢 TenantService: Utilisation des tenants en cache suite à une erreur');
          this.tenants = cachedTenants;
          
          // Mettre à jour le TenantManager avec les tenants en cache
          this.updateTenantManager(this.tenants);
          
          // Récupérer le tenant actuel et le mettre à jour si nécessaire
          const storedTenantId = localStorage.getItem('currentTenantId');
          const currentTenantId = storedTenantId !== null ? storedTenantId : (this.tenants.length > 0 ? this.tenants[0].id : '');
          
          if (currentTenantId && typeof currentTenantId === 'string') {
            localStorage.setItem('currentTenantId', currentTenantId);
          } else if (currentTenantId) {
            localStorage.setItem('currentTenantId', String(currentTenantId));
          }
          
          console.log('✅ TenantService: Récupération des tenants depuis le cache terminée');
          return this.tenants;
        }
      }
      
      // Si nous n'avons pas pu récupérer depuis le cache, retourner un tableau vide
      return [];
    }
  }

  /**
   * Crée un tenant avec un plan spécifique
   * @param name Nom du tenant à créer
   * @param plan Plan à utiliser (free, standard, professional ou enterprise)
   * @param options Options additionnelles (industry, size, country, etc.)
   * @returns Le nouveau tenant créé
   */
  async createTenantWithPlan(name: string, plan: TenantPlan, options: Partial<Omit<Tenant, 'id' | 'name' | 'plan'>> = {}): Promise<Tenant | null> {
    console.log(`🏢 TenantService: Création d'un tenant avec le plan ${plan}`);
    
    // Vérifier d'abord si le plan gratuit est disponible si on essaie de le créer
    if (plan === 'free') {
      const canCreateFree = await this.canCreateTenantWithPlan('free');
      if (!canCreateFree) {
        console.log('L\'utilisateur a déjà une organisation gratuite, tentative avec le plan standard');
        plan = 'standard';
      }
    }
    
    // Préparer les données du tenant
    const tenantData: Partial<Tenant> = {
      name,
      plan,
      slug: this.generateSlug(name),
      settings: {
        industry: options.settings?.industry || 'technology',
        size: options.settings?.size || 'small',
        country: options.settings?.country || 'FR',
        website: options.settings?.website || ''
      },
      status: 'active' as TenantStatus,
      ...options
    };
    
    // Créer le tenant avec le plan spécifié
    try {
      const newTenant = await this.createTenant(tenantData as Tenant, false);
      console.log(`✅ TenantService: Tenant créé avec succès avec le plan ${plan}`);
      return newTenant;
    } catch (error: any) {
      console.error(`❌ TenantService: Erreur lors de la création du tenant avec le plan ${plan}:`, error);
      
      // Détecter spécifiquement l'erreur Django concernant le plan gratuit
      const errorDetails = error?.response?.data?.details || '';
      const errorMessage = error?.response?.data?.error || '';
      
      if (typeof errorDetails === 'string' && 
          (errorDetails.includes('déjà créé une organisation gratuite') || 
           errorDetails.includes('plan') || 
           errorMessage.includes('plan'))) {
        
        console.log('Détection d\'une erreur de plan, tentative avec le plan standard');
        
        // Essayer automatiquement avec le plan standard
        if (plan === 'free') {
          // Mettre à jour le plan dans les données
          tenantData.plan = 'standard';
          
          // Nouvelle tentative avec le plan standard
          try {
            console.log('🔄 TenantService: Nouvelle tentative avec le plan standard');
            const newTenant = await this.createTenant(tenantData as Tenant, false);
            console.log('✅ TenantService: Tenant créé avec succès avec le plan standard');
            return newTenant;
          } catch (retryError) {
            console.error('❌ TenantService: Échec de la nouvelle tentative:', retryError);
            throw retryError;
          }
        }
      }
      
      // Si ce n'est pas une erreur de plan ou si la nouvelle tentative a échoué, propager l'erreur
      throw error;
    }
  }

  /**
   * Synchronise les tenants depuis l'API
   * @param options Options de synchronisation
   * @returns void
   */
  public async synchronizeTenants(options: { force?: boolean } = {}): Promise<void> {
    const { force = false } = options;

    try {
      console.log('🏢 TenantService: Synchronisation des tenants');

      try {
        // Vérifier la limite de taux avant d'effectuer une requête
        this.checkRateLimit();
        
        // Vérifier l'authentification
        const userProfile = TokenManager.getUserProfile();
        if (!userProfile?.id) {
          throw new Error('Données utilisateur manquantes ou incomplètes');
        }
      } catch (authError: unknown) {
        console.error('🏢 TenantService: Erreur lors de l\'authentification:', authError);
        throw authError;
      }
      
      // Utiliser les tenants en cache si disponibles et si on ne force pas le rafraîchissement
      if (!force && this.tenants.length > 0) {
        console.log('🏢 TenantService: Utilisation des tenants en cache');
        return;
      }
      
      // Récupérer via l'API avec gestion des erreurs de limitation de taux
      try {
        const tenants = await this.tenantAPI.getUserTenants();
        
        // Vérifier si la réponse est valide
        if (!Array.isArray(tenants)) {
          console.warn('🏢 TenantService: Format de réponse inattendu:', tenants);
          throw new Error('Format de réponse inattendu');
        }
        
        // Mettre en cache les tenants valides avec filtrage plus strict
        this.tenants = tenants.filter((t): t is Tenant => {
          // Vérifier que le tenant est un objet valide avec un ID
          if (!t || typeof t !== 'object' || !t.id) {
            console.warn('🏢 TenantService: Tenant invalide ignoré:', t);
            return false;
          }
          
          return true;
        });
        
        // Filtrer les tenants de test si en production
        this.tenants = this.filterTestTenants(this.tenants);
        
        // Sauvegarder dans le localStorage pour utilisation ultérieure
        try {
          localStorage.setItem('availableTenants', JSON.stringify(this.tenants));
          localStorage.setItem('lastTenantSyncTimestamp', Date.now().toString());
        } catch (storageError: unknown) {
          console.warn('🏢 TenantService: Erreur lors de la sauvegarde localStorage:', storageError);
        }
        
        // Réinitialiser les compteurs de tentatives car nous avons réussi
        this.resetRateLimitCounters();
        
        // Mettre à jour le TenantManager
        this.updateTenantManager(this.tenants);
        
        // Emettre un événement pour informer les autres composants
        this.emitTenantsUpdated();
        
        console.log('✅ TenantService: Synchronisation terminée avec succès');
      } catch (apiError: unknown) {
        console.error('🏢 TenantService: Erreur API lors de la synchronisation:', apiError);
        throw apiError;
      }
    } catch (error: unknown) {
      console.error('🏢 TenantService: Erreur lors de la synchronisation:', error);
      throw error;
    }
  }
  
  /**
   * Émet un événement pour informer les autres composants du changement de tenants
   */
  private emitTenantsUpdated(): void {
    try {
      if (typeof window !== 'undefined') {
        const event = new CustomEvent('tenantDataChanged', { 
          detail: { 
            tenants: this.tenants,
            currentTenantId: localStorage.getItem('currentTenantId') ?? (this.tenants.length > 0 ? this.tenants[0].id : '')
          } 
        });
        console.log('📢 TenantService - Émission de l\'événement tenantDataChanged');
        window.dispatchEvent(event);
      }
    } catch (error: unknown) {
      console.error('Erreur lors de l\'émission de l\'événement tenantDataChanged:', error);
    }
  }

  /**
   * Sauvegarde les tenants dans le localStorage
   */
  private saveTenantsToLocalStorage(tenants: Tenant[]): void {
    try {
      localStorage.setItem('availableTenants', JSON.stringify(tenants));
      localStorage.setItem('lastTenantSyncTimestamp', Date.now().toString());
      console.log('💾 TenantService - Tenants sauvegardés dans le localStorage:', tenants.length);
    } catch (e: unknown) {
      console.warn('⚠️ TenantService - Erreur lors de la sauvegarde dans localStorage:', e);
    }
  }

  /**
   * Récupère les tenants depuis le localStorage
   */
  private getTenantsFromLocalStorage(): Tenant[] {
    try {
      const storedTenantsJson = localStorage.getItem('availableTenants');
      if (storedTenantsJson) {
        const storedTenants = JSON.parse(storedTenantsJson);
        if (Array.isArray(storedTenants) && storedTenants.length > 0) {
          console.log('💾 TenantService - Tenants récupérés depuis le localStorage:', storedTenants.length);
          return storedTenants;
        }
      }
    } catch (e: unknown) {
      console.warn('⚠️ TenantService - Erreur lors de la récupération depuis localStorage:', e);
    }
    return [];
  }

  /**
   * Met à jour le TenantManager avec les tenants
   */
  private updateTenantManager(tenants: Tenant[]): void {
    if (!tenants || tenants.length === 0) {
      console.warn('⚠️ TenantService - Pas de tenants à mettre à jour dans le TenantManager');
      return;
    }
    
    try {
      // Récupérer l'ID du tenant actuel depuis le localStorage
      const storedTenantId = localStorage.getItem('currentTenantId');
      const currentTenantId = storedTenantId !== null ? storedTenantId : tenants[0].id;

      // Préparer les données pour l'initialisation
      const tenantUserData = {
        tenantId: currentTenantId,
        tenantRole: 'owner', // Rôle par défaut, à ajuster si nécessaire
        tenants: tenants.map(t => ({ tenantId: t.id, role: 'owner' })) // À ajuster si les rôles sont disponibles
      };

      // Initialiser le TenantManager
      tenantManager.initialize(tenantUserData, tenants);
      console.log('🏘️ TenantService - TenantManager initialisé avec', tenants.length, 'tenants');
    } catch (e: unknown) {
      console.error('❌ TenantService - Erreur lors de la mise à jour du TenantManager:', e);
    }
  }

  /**
   * Force la récupération complète des tenants depuis tous les endpoints disponibles
   * Utile après la création d'un tenant pour s'assurer qu'il est visible
   * @param selectMostRecent Si true, sélectionne automatiquement le tenant le plus récemment créé
   * @param newTenantId ID du nouveau tenant à sélectionner (optionnel)
   * @returns Une liste de tous les tenants disponibles après déduplication
   */
  public async forceFullTenantRefresh(selectMostRecent: boolean = false, newTenantId?: string): Promise<Tenant[]> {
    console.log('🔍 TenantService: Forçage de la récupération complète des tenants');
    
    try {
      // Effectuer un scan complet des tenants
      const allTenants = await this.tenantAPI.getUserTenants();
      
      // Mettre à jour la liste des tenants dans le service
      this.tenants = allTenants;
      
      console.log(`✅ TenantService: ${allTenants.length} tenants récupérés après scan complet`);
      
      // Si demandé, sélectionner automatiquement le tenant le plus récent ou le tenant spécifié
      if (selectMostRecent && allTenants.length > 0) {
        try {
          // Import dynamique pour éviter les problèmes de dépendance circulaire
          const tenantManagerModule = await import('./tenant-manager');
          const tenantManager = tenantManagerModule.default;
          
          // Si un ID spécifique est fourni, l'utiliser
          if (newTenantId && allTenants.some(t => t.id === newTenantId)) {
            tenantManager.setCurrentTenant(newTenantId);
            console.log(`🏢 TenantService: Tenant spécifié ${newTenantId} défini comme tenant actif`);
          } 
          // Sinon, trouver le tenant le plus récent
          else {
            // Trier les tenants par date de création décroissante
            const sortedTenants = [...allTenants].sort((a, b) => {
              if (!a.createdAt || !b.createdAt) return 0;
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            });
            
            if (sortedTenants.length > 0) {
              const mostRecentTenant = sortedTenants[0];
              tenantManager.setCurrentTenant(mostRecentTenant.id);
              console.log(`🏢 TenantService: Tenant le plus récent ${mostRecentTenant.name} (${mostRecentTenant.id}) défini comme tenant actif`);
            }
          }
        } catch (selectError) {
          console.error('❌ TenantService: Erreur lors de la sélection du tenant le plus récent:', selectError);
        }
      }
      
      return allTenants;
    } catch (error) {
      console.error('❌ TenantService: Erreur lors du forçage de la récupération des tenants:', error);
      // En cas d'erreur, on essaie de récupérer les tenants sans le mode force
      return this.getAllTenants({});
    }
  }

  /**
   * Filtre les tenants de test en production
   */
  private filterTestTenants(tenants: Tenant[]): Tenant[] {
    // En production, filtrer les tenants de test
    if (process.env.NODE_ENV === 'production') {
      return tenants.filter(tenant => !this.isTestTenant(tenant));
    }

    return tenants;
  }

  /**
   * Récupère un tenant spécifique par son ID
   * @param tenantId ID du tenant à récupérer
   * @returns Le tenant ou null si non trouvé
   */
  public async getTenantById(tenantId: string): Promise<Tenant | null> {
    try {
      if (!tenantId) {
        console.warn('⚠️ TenantService: ID de tenant invalide');
        return null;
      }
      
      // Vérifier d'abord si le tenant est déjà en cache local
      const cachedTenants = this.tenants || [];
      const cachedTenant = cachedTenants.find(t => t.id === tenantId);
      
      if (cachedTenant) {
        console.log('🏢 TenantService: Tenant trouvé dans le cache local');
        return cachedTenant;
      }
      
      // Sinon, essayer de récupérer depuis l'API
      console.log(`🏢 TenantService: Récupération du tenant ${tenantId} via l'API`);
      const tenant = await this.tenantAPI.getTenant(tenantId);
      
      // Mettre à jour le cache si le tenant est trouvé
      if (tenant) {
        // Ajouter au cache local
        const updatedTenants = [...this.tenants];
        const existingIndex = updatedTenants.findIndex(t => t.id === tenant.id);
        
        if (existingIndex >= 0) {
          updatedTenants[existingIndex] = { ...updatedTenants[existingIndex], ...tenant };
        } else {
          updatedTenants.push(tenant);
        }
        
        this.tenants = updatedTenants;
      }
      
      return tenant;
    } catch (error) {
      console.error('❌ TenantService: Erreur lors de la récupération du tenant par ID:', error);
      return null;
    }
  }

  /**
   * Définit un tenant comme tenant par défaut pour l'utilisateur courant
   * @param tenantId ID du tenant à définir comme défaut
   * @returns true si l'opération a réussi, false sinon
   */
  public async setDefaultTenant(tenantId: string): Promise<boolean> {
    try {
      console.log(`🏢 TenantService: Définition du tenant ${tenantId} comme tenant par défaut`);
      
      // Vérifier si l'utilisateur est authentifié
      if (!TokenManager.isAuthenticated()) {
        throw new Error('Utilisateur non authentifié');
      }
      
      // Vérifier si le tenant existe
      const tenant = await this.getTenantById(tenantId);
      if (!tenant) {
        throw new Error(`Le tenant avec l'ID ${tenantId} n'existe pas`);
      }
      
      // Appel API pour définir le tenant par défaut
      const result = await this.tenantAPI.setDefaultTenant(tenantId);
      
      if (result.success) {
        // Mettre à jour le localStorage
        localStorage.setItem('currentTenantId', tenantId);
        localStorage.setItem('sentinel_default_tenant_id', tenantId);
        
        // Déclencher un événement pour informer l'application que le tenant par défaut a changé
        const event = new CustomEvent('tenantDefaultChanged', { detail: { tenantId } });
        window.dispatchEvent(event);
        
        console.log(`✅ TenantService: Tenant ${tenantId} défini comme tenant par défaut avec succès`);
        return true;
      } else {
        throw new Error(result.message || 'Erreur lors de la définition du tenant par défaut');
      }
    } catch (error) {
      console.error('❌ TenantService: Erreur lors de la définition du tenant par défaut:', error);
      
      // En cas d'échec de l'API, tenter de mettre à jour uniquement le localStorage
      try {
        localStorage.setItem('currentTenantId', tenantId);
        localStorage.setItem('sentinel_default_tenant_id', tenantId);
        console.log('⚠️ TenantService: Mise à jour du localStorage malgré l\'échec de l\'API');
      } catch (storageError) {
        console.error('❌ TenantService: Échec également de la mise à jour du localStorage:', storageError);
      }
      
      return false;
    }
  }
}

export default TenantService.getInstance();
